const DAY_MS = 24 * 60 * 60 * 1000

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"]

export function todayIso(): string {
    return new Date().toISOString().slice(0, 10)
}

export function isIsoDate(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`))
}

function toTime(iso: string): number {
    return Date.parse(`${iso.slice(0, 10)}T00:00:00Z`)
}

export function addDays(iso: string, days: number): string {
    return new Date(toTime(iso) + days * DAY_MS).toISOString().slice(0, 10)
}

export function daysBetween(from: string, to: string): number {
    const start = toTime(from)
    const end = toTime(to)

    if (Number.isNaN(start) || Number.isNaN(end)) return 0

    return Math.round((end - start) / DAY_MS)
}

export function formatShortDate(iso: string): string {
    const [, month, day] = iso.split('-').map(Number)

    if (!month || !day || month < 1 || month > 12) return iso

    return `${MONTHS[month - 1]} ${day}`
}

export function formatLongDate(iso: string): string {
    const [year, month, day] = iso.split('-').map(Number)

    if (!year || !month || !day || month < 1 || month > 12) return iso

    return `${MONTHS[month - 1]} ${day}, ${year}`
}

export type Quarter = {
    key: string
    year: number
    quarter: number
    label: string
    start: string
    end: string
    months: string[]
}

export function quarterFromParts(year: number, quarter: number): Quarter {
    const firstMonth = (quarter - 1) * 3 + 1
    const months = [0, 1, 2].map(offset => `${year}-${String(firstMonth + offset).padStart(2, '0')}`)
    const lastMonth = firstMonth + 2
    const lastDay = new Date(Date.UTC(year, lastMonth, 0)).getUTCDate()

    return {
        key: `${year}-q${quarter}`,
        year,
        quarter,
        label: `Q${quarter} ${year}`,
        start: `${months[0]}-01`,
        end: `${months[2]}-${String(lastDay).padStart(2, '0')}`,
        months
    }
}

export function quarterOf(iso: string): Quarter {
    const [year, month] = iso.split('-').map(Number)

    return quarterFromParts(year, Math.floor(((month || 1) - 1) / 3) + 1)
}

export function quarterFromKey(key: string): Quarter | null {
    const match = /^(\d{4})-q([1-4])$/i.exec(key)

    if (!match) return null

    return quarterFromParts(Number(match[1]), Number(match[2]))
}

export function shiftQuarter(quarter: Quarter, offset: number): Quarter {
    const index = quarter.year * 4 + (quarter.quarter - 1) + offset

    return quarterFromParts(Math.floor(index / 4), (index % 4) + 1)
}

export function nextFirstQuarter(today: string): Quarter {
    const current = quarterOf(today)

    return current.quarter === 1 ? current : quarterFromParts(current.year + 1, 1)
}

export function monthLabel(month: string): string {
    const index = Number(month.split('-')[1]) - 1

    return MONTHS[index] ?? month
}
